import { rtcEvents } from "../protocol/events/rtc-events";
import { ArtichokeApi } from "../artichoke/artichoke-api";
import type { ID, TurnConfig } from "../protocol/protocol";
import {
  ConnectionStatus,
  RTCPeerConnectionFacade,
} from "./rtc-peer-connection-facade";
import { Observable, Subject } from "rxjs";
import { filter } from "rxjs/operators";
import { LoggerFactory } from "../logger/logger-factory";
import { LoggerService } from "../logger/logger-service";
import { WebRTCStats } from "./stats/webrtc-stats";
import { Queue } from "../utils/queue";
import { Delayer } from "../utils/delayer";

export interface RecordingRemoteTrack {
  readonly peerId: ID;
  readonly track: MediaStreamTrack;
  readonly connectionType: "offer-sender" | "answer-sender";
}

export interface RecordingPeerConnectionStatus {
  readonly peerId: ID;
  readonly status: ConnectionStatus;
  readonly connectionType: "offer-sender" | "answer-sender";
}

export class RecordingRTCPool {
  // Connection that sends offers and listens for answers
  private readonly offerSenderConnections: Map<ID, RTCPeerConnectionFacade> =
    new Map();
  // Connection that receives offers and sends answers
  private readonly answerSenderConnections: Map<ID, RTCPeerConnectionFacade> =
    new Map();

  // tslint:disable-next-line:readonly-keyword
  private tracks: ReadonlyArray<MediaStreamTrack> = [];
  private readonly remoteTrackEvent = new Subject<RecordingRemoteTrack>();
  private readonly connectionStatusEvent =
    new Subject<RecordingPeerConnectionStatus>();

  private keepAliveTimer: any = null;
  private readonly keepAliveIntervalMs: number;

  private readonly logger: LoggerService;
  // Cache TURN config to avoid multiple API calls
  private turnConfigPromise: Promise<TurnConfig> | null = null;

  constructor(
    public readonly callId: ID,
    private rtcConfig: RTCConfiguration,
    private loggerFactory: LoggerFactory,
    private artichokeApi: ArtichokeApi,
    private webrtcStats: WebRTCStats,
    keepAliveIntervalMs: number
  ) {
    this.logger = loggerFactory.create(`RecordingRTCPool(${callId})`);
    this.keepAliveIntervalMs = keepAliveIntervalMs || 30000;

    // FIXME - unsubscribe
    this.descriptionSent$.subscribe(this.listenForDescriptionSent);
    // FIXME - unsubscribe
    this.candidateSent$.subscribe(this.listenForCandidateSent);

    // Subscribe to remote track events for audio playback (similar to JS ontrack handler)
    this.remoteTrack$.subscribe(this.handleRemoteTrackForAudioPlayback);

    // Subscribe to connection status events for cleanup (similar to JS onconnectionstatechange)
    this.peerStatus$.subscribe(this.handleConnectionStatusChange);
  }

  public get peerStatus$(): Observable<RecordingPeerConnectionStatus> {
    return this.connectionStatusEvent;
  }

  public get remoteTrack$(): Observable<RecordingRemoteTrack> {
    return this.remoteTrackEvent;
  }

  public addTrack(track: MediaStreamTrack): void {
    this.tracks = [...this.tracks, track];
    this.offerSenderConnections.forEach((peerConnection) =>
      peerConnection.addTrack(track)
    );
    this.answerSenderConnections.forEach((peerConnection) =>
      peerConnection.addTrack(track)
    );
  }

  public removeTrack(track: MediaStreamTrack): void {
    this.tracks = this.tracks.filter((t) => t !== track);
    this.offerSenderConnections.forEach((peerConnection) =>
      peerConnection.removeTrack(track)
    );
    this.answerSenderConnections.forEach((peerConnection) =>
      peerConnection.removeTrack(track)
    );
  }

  public async connectAsOfferSender(
    peerId: ID,
    receiveAudio: boolean = true
  ): Promise<void> {
    this.logger.debug(
      `Connecting as offer-sender to ${peerId} with ${
        receiveAudio ? "sendrecv" : "sendonly"
      } direction`
    );

    // Allow configurable audio receiving for peer-to-peer vs recording scenarios
    const offerOptions: RTCOfferOptions = {
      offerToReceiveAudio: receiveAudio,
      offerToReceiveVideo: false,
    };

    return (await this.getOfferSenderConnection(peerId)).offer(offerOptions);
  }

  public async connectAsAnswerSender(peerId: ID): Promise<void> {
    this.logger.debug(
      `Connecting as answer-sender to ${peerId} - will handle bidirectional offers`
    );
    // For answer sender, we prepare the connection to handle offers when they come
    // This connection will receive offers and respond with bidirectional answers
    await this.getAnswerSenderConnection(peerId);
  }

  /**
   * Convenience method for peer-to-peer bidirectional communication
   * Use this instead of connectAsOfferSender when you want to hear each other's audio
   */
  public async connectAsPeerToPeer(peerId: ID): Promise<void> {
    this.logger.debug(
      `Connecting as peer-to-peer to ${peerId} with bidirectional audio`
    );

    // Use the updated connectAsOfferSender with receiveAudio = true for bidirectional communication
    return this.connectAsOfferSender(peerId, true);
  }

  public destroyConnection(peerId: ID): void {
    const offerSenderConnection = this.offerSenderConnections.get(peerId);
    const answerSenderConnection = this.answerSenderConnections.get(peerId);

    // Clean up audio elements first
    this.cleanupAudioElementForPeer(peerId);

    if (offerSenderConnection) {
      this.logger.debug(
        `Destroying offer-sender connection for peerId ${peerId}`
      );
      offerSenderConnection.disconnect();
      this.offerSenderConnections.delete(peerId);
    }

    if (answerSenderConnection) {
      this.logger.debug(
        `Destroying answer-sender connection for peerId ${peerId}`
      );
      answerSenderConnection.disconnect();
      this.answerSenderConnections.delete(peerId);
    }

    if (!offerSenderConnection && !answerSenderConnection) {
      this.logger.warn(
        `Cannot destroy connections for peerId ${peerId} - they do not exist`
      );
    }

    this.ensureKeepAliveTimerStopped();
  }

  public destroyAllConnections(): void {
    this.logger.debug(`Destroying all recording connections`);

    this.stopKeepAliveTimer();

    // Clean up audio elements for all peers
    const allPeerIds = new Set([
      ...this.offerSenderConnections.keys(),
      ...this.answerSenderConnections.keys(),
    ]);

    allPeerIds.forEach((peerId) => {
      this.cleanupAudioElementForPeer(peerId);
    });

    this.offerSenderConnections.forEach((_, peerId) =>
      this.destroyConnection(peerId)
    );
    this.answerSenderConnections.forEach((_, peerId) =>
      this.destroyConnection(peerId)
    );
  }

  public async replaceTrackByKind(track: MediaStreamTrack): Promise<void> {
    const offerSenderPromises = Array.from(this.offerSenderConnections)
      .map((peerIdPeerConnectionPair) => peerIdPeerConnectionPair[1])
      .map((peerConnection) => peerConnection.replaceTrackByKind(track));

    const answerSenderPromises = Array.from(this.answerSenderConnections)
      .map((peerIdPeerConnectionPair) => peerIdPeerConnectionPair[1])
      .map((peerConnection) => peerConnection.replaceTrackByKind(track));

    return Promise.all([...offerSenderPromises, ...answerSenderPromises]).then(
      (_) => undefined
    );
  }

  // tslint:disable-next-line:readonly-keyword
  private listenForDescriptionSent = async (
    msg: rtcEvents.DescriptionSent
  ): Promise<void> => {
    // Check if SDP contains sendonly/recvonly/sendrecv directions
    const sdpDirection = msg.sdp.sdp?.includes("sendonly")
      ? "sendonly"
      : msg.sdp.sdp?.includes("recvonly")
      ? "recvonly"
      : msg.sdp.sdp?.includes("sendrecv")
      ? "sendrecv"
      : "unknown";

    this.logger.debug(
      `RecordingRTCPool received ${msg.sdp.type} from ${msg.sender} with direction: ${sdpDirection}`
    );

    switch (msg.sdp.type) {
      case "offer":
        // Offers are handled by answer-sender connections
        return (await this.getAnswerSenderConnection(msg.sender)).handleRemoteOffer(
          msg.sdp
        );
      case "answer":
        // Answers are handled by offer-sender connections
        return (await this.getOfferSenderConnection(msg.sender)).handleRemoteAnswer(
          msg.sdp
        );
      default:
        const error = `Received an invalid RTC description type ${msg.sdp.type}`;
        this.logger.error(error);
        return Promise.reject(error);
    }
  };

  // tslint:disable-next-line:readonly-keyword
  private listenForCandidateSent = (msg: rtcEvents.CandidateSent): void => {
    this.logger.debug(
      `RecordingRTCPool received an RTC candidate from ${msg.sender}`
    );

    // Try to add candidate to both connection types (only the active one will use it)
    const offerSenderConnection = this.offerSenderConnections.get(msg.sender);
    const answerSenderConnection = this.answerSenderConnections.get(msg.sender);

    if (offerSenderConnection) {
      offerSenderConnection.addCandidate(msg.candidate);
    }
    if (answerSenderConnection) {
      answerSenderConnection.addCandidate(msg.candidate);
    }
  };

  private get descriptionSent$(): Observable<rtcEvents.DescriptionSent> {
    return this.getRtcPoolEvent().pipe(filter(rtcEvents.DescriptionSent.is));
  }

  private get candidateSent$(): Observable<rtcEvents.CandidateSent> {
    return this.getRtcPoolEvent().pipe(filter(rtcEvents.CandidateSent.is));
  }

  // tslint:disable-next-line:readonly-keyword
  private getRtcPoolEvent = (): Observable<rtcEvents.RTCSignallingEvent> =>
    this.artichokeApi.domainEvent$
      .pipe(filter(rtcEvents.RTCSignallingEvent.is))
      .pipe(filter((e) => e.callId === this.callId));

  // tslint:disable-next-line:readonly-keyword
  private getOfferSenderConnection = async (peerId: ID): Promise<RTCPeerConnectionFacade> => {
    const maybeConnection = this.offerSenderConnections.get(peerId);
    if (maybeConnection) {
      this.logger.debug(
        `Offer-sender connection exists for peer ${peerId} - returning`
      );
      return maybeConnection;
    } else {
      this.logger.debug(
        `Creating new offer-sender connection for peer ${peerId}`
      );
      const rtcConnection = await this.createRTCConnectionFacade(
        peerId,
        "offer-sender"
      );
      this.offerSenderConnections.set(peerId, rtcConnection);

      this.ensureKeepAliveTimerRunning();

      return rtcConnection;
    }
  };

  // tslint:disable-next-line:readonly-keyword
  private getAnswerSenderConnection = async (peerId: ID): Promise<RTCPeerConnectionFacade> => {
    const maybeConnection = this.answerSenderConnections.get(peerId);
    if (maybeConnection) {
      this.logger.debug(
        `Answer-sender connection exists for peer ${peerId} - returning`
      );
      return maybeConnection;
    } else {
      this.logger.debug(
        `Creating new answer-sender connection for peer ${peerId}`
      );
      const rtcConnection = await this.createRTCConnectionFacade(
        peerId,
        "answer-sender"
      );
      this.answerSenderConnections.set(peerId, rtcConnection);

      this.ensureKeepAliveTimerRunning();

      return rtcConnection;
    }
  };

  // tslint:disable-next-line:readonly-keyword
  private getTrackEventHandler =
    (
      peerId: ID,
      connectionType: "offer-sender" | "answer-sender"
    ): ((track: MediaStreamTrack) => void) =>
    (track: MediaStreamTrack): void =>
      this.remoteTrackEvent.next({ peerId, track, connectionType });

  // tslint:disable-next-line:readonly-keyword
  private getConnectionStatusEventHandler =
    (
      peerId: ID,
      connectionType: "offer-sender" | "answer-sender"
    ): ((status: ConnectionStatus) => void) =>
    (status: ConnectionStatus): void =>
      this.connectionStatusEvent.next({ peerId, status, connectionType });

  private async createRTCConnectionFacade(
    peerId: ID,
    connectionType: "offer-sender" | "answer-sender"
  ): Promise<RTCPeerConnectionFacade> {
    this.logger.debug(
      `Creating new ${connectionType} RTCConnection for peerId: ${peerId}`
    );
    const peerCandidateQueueLogger = this.loggerFactory.create(
      `${connectionType}-PeerCandidateQueue Peer(${peerId})`
    );

    // Use cached TURN config to avoid multiple API calls
    if (!this.turnConfigPromise) {
      this.logger.debug(`Fetching TURN config for call ${this.callId}`);
      this.turnConfigPromise = this.artichokeApi.getTurnConfig();
    }
    const turnConfig = await this.turnConfigPromise;

    // Create a new config object with TURN servers to avoid mutating shared config
    const peerRtcConfig: RTCConfiguration = {
      ...this.rtcConfig,
      iceServers: [
        {
          urls: turnConfig.urls,
          username: turnConfig.username,
          credential: turnConfig.password
        }
      ]
    };
    const rtcPeerConnection = new RTCPeerConnection(peerRtcConfig);

    // Configure transceivers for bidirectional audio communication
    this.configureTransceivers(rtcPeerConnection, connectionType);

    return new RTCPeerConnectionFacade(
      this.callId,
      peerId,
      rtcPeerConnection,
      this.artichokeApi,
      this.getTrackEventHandler(peerId, connectionType),
      this.getConnectionStatusEventHandler(peerId, connectionType),
      new Queue<RTCIceCandidateInit>(peerCandidateQueueLogger),
      this.loggerFactory.create(
        `${connectionType}-RTCPeerConnectionFacade Call(${this.callId}) Peer(${peerId})`
      ),
      new Delayer(),
      this.tracks,
      this.webrtcStats
    );
  }

  private configureTransceivers(
    rtcPeerConnection: RTCPeerConnection,
    connectionType: "offer-sender" | "answer-sender"
  ): void {
    this.logger.debug(
      `Configuring transceivers for ${connectionType} connection`
    );

    if (connectionType === "offer-sender") {
      // Offer sender: can send and receive audio (sendrecv)
      rtcPeerConnection.addTransceiver("audio", {
        direction: "sendrecv",
      });
    } else {
      // Answer sender: can send and receive audio (sendrecv)
      rtcPeerConnection.addTransceiver("audio", {
        direction: "sendrecv",
      });
    }
  }

  // Handle remote tracks for audio playback (similar to JS ontrack handler)
  private handleRemoteTrackForAudioPlayback = (
    remoteTrack: RecordingRemoteTrack
  ): void => {
    this.logger.debug(
      `Handling remote ${remoteTrack.track.kind} track from ${remoteTrack.peerId} (${remoteTrack.connectionType})`
    );

    if (remoteTrack.track.kind === "audio") {
      this.logger.debug(
        `Setting up audio playback for ${remoteTrack.connectionType} track from ${remoteTrack.peerId}`
      );

      // Create a MediaStream from the track for audio playback
      const audioStream = new MediaStream([remoteTrack.track]);
      this.setupAudioPlayback(
        audioStream,
        remoteTrack.peerId,
        remoteTrack.connectionType
      );
      this.setupTrackEventListeners(
        remoteTrack.track,
        remoteTrack.peerId,
        remoteTrack.connectionType
      );
    }
  };

  // Handle connection status changes for cleanup (similar to JS onconnectionstatechange)
  private handleConnectionStatusChange = (
    statusChange: RecordingPeerConnectionStatus
  ): void => {
    this.logger.debug(
      `Connection status change for ${statusChange.peerId} (${statusChange.connectionType}): ${statusChange.status}`
    );

    // Clean up audio elements when connection fails or disconnects
    if (
      statusChange.status === ConnectionStatus.Failed ||
      statusChange.status === ConnectionStatus.Disconnected
    ) {
      this.logger.debug(
        `Cleaning up audio for ${statusChange.peerId} due to connection ${statusChange.status}`
      );
      this.cleanupAudioElementForPeer(statusChange.peerId);
    } else if (statusChange.status === ConnectionStatus.Connected) {
      this.logger.info(
        `${statusChange.connectionType} connection to ${statusChange.peerId} established`
      );
    }
  };

  private setupAudioPlayback(
    stream: MediaStream,
    peerId: ID,
    connectionType: "offer-sender" | "answer-sender"
  ): void {
    try {
      // Create audio element for playback (similar to JS implementation)
      const audioElementId = `audio_${peerId}_${connectionType}`;
      let audioElement = document.getElementById(
        audioElementId
      ) as HTMLAudioElement;

      if (!audioElement) {
        audioElement = document.createElement("audio");
        audioElement.id = audioElementId;
        audioElement.autoplay = true;
        audioElement.controls = false;
        audioElement.style.display = "none";
        document.body.appendChild(audioElement);
        this.logger.debug(
          `Created audio element for ${peerId} ${connectionType}`
        );
      }

      // Set audio source
      audioElement.srcObject = stream;

      // Handle autoplay restrictions (similar to JS implementation)
      audioElement
        .play()
        .then(() => {
          this.logger.info(
            `Audio playback started for ${peerId} ${connectionType}`
          );
        })
        .catch((error) => {
          this.logger.warn(`Autoplay blocked for ${peerId}: ${error.message}`);

          // Set up user interaction handler for audio enablement
          this.setupAudioEnablementHandler(
            audioElement,
            peerId,
            connectionType
          );
        });
    } catch (error) {
      this.logger.error(
        `Failed to setup audio playback for ${peerId}: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  }

  private setupTrackEventListeners(
    track: MediaStreamTrack,
    peerId: ID,
    connectionType: "offer-sender" | "answer-sender"
  ): void {
    // Monitor track mute state changes (similar to JS implementation)
    track.onmute = () => {
      this.logger.debug(`Audio track muted for ${peerId} ${connectionType}`);
    };

    track.onunmute = () => {
      this.logger.debug(`Audio track unmuted for ${peerId} ${connectionType}`);
    };

    track.onended = () => {
      this.logger.debug(`Audio track ended for ${peerId} ${connectionType}`);
      this.cleanupAudioElementForPeer(peerId);
    };
  }

  private setupAudioEnablementHandler(
    audioElement: HTMLAudioElement,
    peerId: ID,
    connectionType: "offer-sender" | "answer-sender"
  ): void {
    const enableAudio = async () => {
      try {
        await audioElement.play();
        this.logger.info(
          `Audio enabled for ${peerId} ${connectionType} via user interaction`
        );
        document.removeEventListener("click", enableAudio);
        document.removeEventListener("keydown", enableAudio);
      } catch (error) {
        this.logger.error(
          `Failed to enable audio for ${peerId}: ${
            error instanceof Error ? error.message : "Unknown error"
          }`
        );
      }
    };

    // Listen for user interaction to enable audio
    document.addEventListener("click", enableAudio, { once: true });
    document.addEventListener("keydown", enableAudio, { once: true });
  }

  private cleanupAudioElementForPeer(peerId: ID): void {
    // Clean up audio elements for both connection types
    const connectionTypes: Array<"offer-sender" | "answer-sender"> = [
      "offer-sender",
      "answer-sender",
    ];

    connectionTypes.forEach((connectionType) => {
      const audioElementId = `audio_${peerId}_${connectionType}`;
      const audioElement = document.getElementById(
        audioElementId
      ) as HTMLAudioElement;

      if (audioElement) {
        audioElement.pause();
        audioElement.srcObject = null;
        audioElement.remove();
        this.logger.debug(
          `Cleaned up audio element for ${peerId} ${connectionType}`
        );
      }
    });
  }

  private startKeepAliveTimer(): void {
    this.logger.debug(
      `Starting keep-alive timer for recording connections with interval: ${this.keepAliveIntervalMs}ms`
    );

    this.keepAliveTimer = setInterval(() => {
      this.sendKeepAliveMessages();
    }, this.keepAliveIntervalMs);
  }

  private stopKeepAliveTimer(): void {
    if (this.keepAliveTimer) {
      this.logger.debug("Stopping keep-alive timer for recording connections");
      clearInterval(this.keepAliveTimer);
      this.keepAliveTimer = null;
    }
  }

  private sendKeepAliveMessages(): void {
    const hasActiveConnections = this.hasActiveConnections();

    if (!hasActiveConnections) {
      this.logger.debug("No active recording connections, skipping keep-alive");
      return;
    }

    const peerId = this.getFirstPeerId();
    if (!peerId) {
      this.logger.debug("No peer ID found for keep-alive");
      return;
    }

    this.logger.debug(`Sending keep-alive for recording peer ${peerId}`);

    try {
      this.artichokeApi.sendKeepAlive(this.callId, peerId);
      this.logger.debug(`Sent keep-alive for recording peer ${peerId}`);
    } catch (error) {
      this.logger.error(
        `Failed to send keep-alive for recording peer ${peerId}: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  }

  private hasActiveConnections(): boolean {
    return (
      this.offerSenderConnections.size > 0 ||
      this.answerSenderConnections.size > 0
    );
  }

  private getFirstPeerId(): ID | undefined {
    for (const [peerId, connection] of this.offerSenderConnections) {
      if (connection) {
        return peerId;
      }
    }

    for (const [peerId, connection] of this.answerSenderConnections) {
      if (connection) {
        return peerId;
      }
    }

    return undefined;
  }

  private ensureKeepAliveTimerRunning(): void {
    const hasConnections = this.hasActiveConnections();

    if (hasConnections && !this.keepAliveTimer) {
      this.logger.debug(
        "Starting keep-alive timer - active connections detected"
      );
      this.startKeepAliveTimer();
    }
  }

  private ensureKeepAliveTimerStopped(): void {
    const hasConnections = this.hasActiveConnections();

    if (!hasConnections && this.keepAliveTimer) {
      this.logger.debug("Stopping keep-alive timer - no active connections");
      this.stopKeepAliveTimer();
    }
  }
}
